import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { recordFeltRecent } from "@/lib/felt-storage";
import { SaveToProject } from "@/components/felt/SaveToProject";
import {
  FieldCard,
  FieldNumber,
  StatusRow,
  parseNum,
} from "@/components/felt/FieldInput";
import {
  gipsCalc,
  isolasjonCalc,
  stendereCalc,
  vindsperreCalc,
} from "@/domain/calculators";

export const Route = createFileRoute("/felt/vegg")({
  head: () => ({
    meta: [
      { title: "Vegg – materialer | VERQO felt" },
      {
        name: "description",
        content:
          "Regn ut stendere, isolasjon, dampsperre og gips for en vegg direkte på byggeplass.",
      },
      { property: "og:title", content: "Vegg – materialer" },
      {
        property: "og:description",
        content: "Stendere, isolasjon, dampsperre og gips på én skjerm i feltmodus.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: FeltVegg,
});

function fmt(n: number, d = 1) {
  return n.toLocaleString("nb-NO", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function FeltVegg() {
  const [length, setLength] = useState("");
  const [height, setHeight] = useState("2,4");
  const [cc, setCc] = useState("600");
  const [openings, setOpenings] = useState("0");
  const [thickness, setThickness] = useState("198");
  const [pkg, setPkg] = useState("5,4");
  const [layers, setLayers] = useState("1");
  const [spill, setSpill] = useState("10");

  const result = useMemo(() => {
    const L = parseNum(length);
    const H = parseNum(height);
    const CC = parseNum(cc);
    const T = parseNum(thickness);
    const P = parseNum(pkg);
    if (L === null || H === null || CC === null || T === null || P === null) return null;
    if (L <= 0 || H <= 0 || CC <= 0 || P <= 0) return null;

    const area = L * H;
    const sp = parseNum(spill) ?? 10;
    return {
      area,
      stendere: stendereCalc({
        vegg_lengde_m: L,
        vegg_hoyde_m: H,
        cc_mm: CC,
        antall_apninger: parseNum(openings) ?? 0,
      }),
      isolasjon: isolasjonCalc({
        area_m2: area,
        tykkelse_mm: T,
        m2_per_pakke: P,
        spill_pct: 5,
      }),
      dampsperre: vindsperreCalc({
        area_m2: area,
        rull_bredde_m: 2.7,
        rull_lengde_m: 50,
      }),
      gips: gipsCalc({
        area_m2: area,
        layers: parseNum(layers) ?? 1,
        spill_pct: sp,
      }),
    };
  }, [length, height, cc, openings, thickness, pkg, layers, spill]);

  useEffect(() => {
    if (!result) return;
    const t = window.setTimeout(() => {
      recordFeltRecent({
        to: "/felt/vegg",
        title: "Vegg – materialer",
        summary: `${fmt(result.area)} m² · ${result.stendere.stendere} stendere · ${result.gips.plater} gipsplater`,
      });
    }, 800);
    return () => window.clearTimeout(t);
  }, [result]);

  const lines = result
    ? [
        `Vegg ${length} × ${height} m (${fmt(result.area)} m²), cc ${cc} mm`,
        `Stendere: ${result.stendere.stendere} stk (${fmt(result.stendere.stender_lm)} lm)`,
        `Svill: ${fmt(result.stendere.svill_lm)} lm`,
        `Isolasjon: ${result.isolasjon.pakker} pakker (${fmt(result.isolasjon.volum_m3, 2)} m³)`,
        `Dampsperre: ${result.dampsperre.ruller} rull, tape ${result.dampsperre.tape_m} m`,
        `Gips: ${result.gips.plater} plater, ${result.gips.skruer} skruer, sparkel ${fmt(result.gips.sparkel_kg)} kg`,
      ]
    : [];

  return (
    <>
      <Link
        to="/felt"
        className="mb-3 inline-flex h-12 items-center text-sm text-muted-foreground"
      >
        <ChevronLeft className="h-5 w-5" /> Feltmodus
      </Link>
      <h1 className="mb-4 text-2xl font-bold tracking-tight">Vegg – materialer</h1>

      <div className="space-y-4">
        <FieldCard title="Vegg">
          <div className="grid grid-cols-2 gap-3">
            <FieldNumber label="Lengde" unit="m" value={length} onChange={setLength} />
            <FieldNumber label="Høyde" unit="m" value={height} onChange={setHeight} />
            <FieldNumber label="Senteravstand" unit="mm" value={cc} onChange={setCc} />
            <FieldNumber
              label="Åpninger (vindu/dør)"
              unit="stk"
              value={openings}
              onChange={setOpenings}
            />
            <FieldNumber
              label="Isolasjonstykkelse"
              unit="mm"
              value={thickness}
              onChange={setThickness}
            />
            <FieldNumber label="m² per pakke" unit="m²" value={pkg} onChange={setPkg} />
            <FieldNumber label="Gipslag" unit="lag" value={layers} onChange={setLayers} />
            <FieldNumber label="Svinn gips" unit="%" value={spill} onChange={setSpill} />
          </div>
        </FieldCard>

        {!result && (
          <p className="card px-4 py-6 text-center text-sm text-muted-foreground">
            Fyll inn lengde og høyde for å se materialbehovet.
          </p>
        )}

        {result && (
          <>
            <FieldCard title="Bindingsverk">
              <div className="space-y-2">
                <StatusRow label="Veggflate" value={`${fmt(result.area)} m²`} />
                <StatusRow label="Stendere" value={`${result.stendere.stendere} stk`} />
                <StatusRow
                  label="Stenderlengde"
                  value={`${fmt(result.stendere.stender_lm)} lm`}
                />
                <StatusRow label="Svill (topp + bunn)" value={`${fmt(result.stendere.svill_lm)} lm`} />
                <StatusRow label="Total trelast" value={`${fmt(result.stendere.total_lm)} lm`} />
              </div>
            </FieldCard>

            <FieldCard title="Isolasjon og dampsperre">
              <div className="space-y-2">
                <StatusRow label="Isolasjon" value={`${result.isolasjon.pakker} pakker`} />
                <StatusRow label="Volum" value={`${fmt(result.isolasjon.volum_m3, 2)} m³`} />
                <StatusRow label="Dampsperre" value={`${result.dampsperre.ruller} rull`} />
                <StatusRow label="Tape" value={`${result.dampsperre.tape_m} m`} />
              </div>
            </FieldCard>

            <FieldCard title="Gips">
              <div className="space-y-2">
                <StatusRow label="Plater" value={`${result.gips.plater} stk`} />
                <StatusRow label="Skruer" value={`${result.gips.skruer} stk`} />
                <StatusRow label="Sparkel" value={`${fmt(result.gips.sparkel_kg)} kg`} />
                <StatusRow label="Papirtape" value={`${result.gips.tape_m} m`} />
              </div>
            </FieldCard>

            <SaveToProject
              result={{
                title: "Vegg – materialer",
                lines,
                meta: {
                  lengde_m: parseNum(length),
                  hoyde_m: parseNum(height),
                  cc_mm: parseNum(cc),
                  areal_m2: result.area,
                  stendere: result.stendere.stendere,
                  isolasjon_pakker: result.isolasjon.pakker,
                  gipsplater: result.gips.plater,
                },
              }}
            />
          </>
        )}
      </div>
    </>
  );
}
